import {
  focusManager,
  isServer,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";

import { AuthProvider } from "@/contexts/AuthContext";
import { NurseryProvider } from "@/contexts/NurseryContext";
import { OrganisationProvider } from "@/contexts/OrganisationContext";
import { CSPostHogProvider } from "@/lib/posthog";

import { ThemeProvider } from "./theme-provider";

// Completely disable React Query's focus detection globally
if (!isServer) {
  // Override the event listener to do nothing
  focusManager.setEventListener(() => () => {});
  // Set focused state to undefined so it never triggers refetches
  focusManager.setFocused(undefined);
}

/**
 * Extracts an HTTP status code from a thrown query error (axios-shaped errors
 * from the Orval client, with a fallback for a bare `status`).
 */
function errorStatus(error: unknown): number | undefined {
  const e = error as { response?: { status?: number }; status?: number };
  return e?.response?.status ?? e?.status;
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes - prevents unnecessary refetches
        gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache longer
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        // Never retry 4xx: auth (401), plan/permission (403) and not-found (404)
        // responses won't change on retry. The default (3 retries with
        // exponential backoff) turned a single gated request into 4 calls and
        // held loading spinners open for several seconds. Server/network errors
        // (5xx / no response) still get a couple of retries.
        retry: (failureCount, error) => {
          const status = errorStatus(error);
          if (status !== undefined && status >= 400 && status < 500)
            return false;
          return failureCount < 2;
        },
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

export default function Providers({ children }: { children: React.ReactNode }) {
  const queryClient = getQueryClient();

  return (
    <CSPostHogProvider>
      <ThemeProvider
        enableSystem
        attribute="class"
        defaultTheme="system"
        disableTransitionOnChange
      >
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <OrganisationProvider>
              <NurseryProvider>{children}</NurseryProvider>
            </OrganisationProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </CSPostHogProvider>
  );
}
